import React, { useCallback, useEffect, useRef, useState } from 'react'
import axios from 'axios';
import { categories_products, getAllCategories, getAllProductsSave } from '../../lib/apis';
import CustomButton from '../../components/customButton';
import ProductCard from "../../components/productCard";
import { useTranslation } from 'react-i18next';
import { FaArrowLeft, FaSlidersH, FaTimes, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router-dom';
import Breadcrumb from '../../components/Breadcrumb';
import Nav from '../../components/nav';
import { CategorieInterface } from '../../lib/Interfaces/Categorie.interface';
import SparkleImage from '../../components/SparkleImage';
import Modal from '../../components/Modal';
import { toast } from 'react-toastify';
import { scrollToTop } from '../../lib/utils';
import { Grid, List, Filter, Search, X, ChevronRight } from 'lucide-react';
import DraggableProgressBar from '../../components/EditableProgressBar ';

function Products() {
    const [products, setProducts] = useState<any[]>([])
    const { t, i18n } = useTranslation()
    const navigate = useNavigate()
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [isOpen, setIsOpen] = useState(false);
    const [valueToSearch, setValueToSearch] = useState({
        region_id: null,
        departement_id: null,
        prix_min: null,
        prix_max: null
    })
    const observer = useRef<IntersectionObserver | null>(null);
    const [subCategories, setSubCategories] = useState<any[]>([])
    const labelSearch = "Tout"
    const searchInput = useLocation().state
    const [value, setValue] = useState(labelSearch)
    const [isSearching, setIsSearching] = useState(false);
    const [searchPage, setSearchPage] = useState(1);
    const [hasMoreSearchResults, setHasMoreSearchResults] = useState(true);
    const [allDataSubCat, setAllDataSubCat] = useState<any>()
    const [filterProduct, setFilterproduct] = useState<any[]>([])
    const [allDataCat, setAllDataCat] = useState<any>()
    const [categories, setCategories] = useState<CategorieInterface[]>([])
    const [loading, setLoading] = useState(false)
    const [index, setIndex] = useState<number>(0)
    const loaderRef = useRef<HTMLDivElement>(null);
    const [isNavbarShrunk, setIsNavbarShrunk] = useState(false);
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
    const [searchQuery, setSearchQuery] = useState('');
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState<string>('all');
    const [isPriceFilterOpen, setIsPriceFilterOpen] = useState(false);

      useEffect(() => {
        scrollToTop()
      }, [])

    // Navbar scroll effect
    useEffect(() => {
        const handleScroll = () => {
            setIsNavbarShrunk(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Infinite scroll implementation
    const loadMoreProducts = useCallback(async () => {
        if (loading || !hasMore || isSearching) return;
        setLoading(true);
        try {
            const { data } = index === 0
                ? await axios.get(`${getAllProductsSave}?limit=12&page=${page}`)
                : await axios.get(`${categories_products}/${index}/products?limit=12&page=${page}}`);

            if (data.products.length === 0) {
                setHasMore(false);
                return;
            }

            setProducts(prev => [...prev, ...data.products]);
            setPage(prev => prev + 1);

            if (searchInput) {
                const label = searchInput.toLocaleLowerCase();
                setFilterproduct(prev => [...prev, ...data.products.filter((item: any) =>
                    item.product_name.toLocaleLowerCase().includes(label) ||
                    item.sale_name.toLocaleLowerCase().includes(label) ||
                    item.variety.toLocaleLowerCase().includes(label)
                )]);
            } else {
                setFilterproduct(prev => [...prev, ...data.products]);
            }
        } catch (error: any) {
            toast.error(i18n.language === "fr" ? error.response.data.message : error.response.data.messageE)
        } finally {
            setLoading(false);
        }
    }, [page, loading, hasMore, searchInput, index, i18n.language]);

    const handleSearch = useCallback(async (pageNum: number = 1, reset: boolean = false, searchInput: any, categorie_id: number) => {
        setIsOpen(false)
        setIsSearching(true)
        setLoading(true)
        try {
            const endpoint = categorie_id === 0
                ? `${getAllProductsSave}?limit=12&page=${pageNum}&minPrice=${searchInput.prix_max || ''}&region_id=${searchInput.region_id || ''}&departement_id=${searchInput.departement_id || ''}`
                : `${categories_products}/${categorie_id}/products?limit=12&page=${pageNum}&minPrice=${searchInput.prix_max || ''}&region_id=${searchInput.region_id || ''}&departement_id=${searchInput.departement_id || ''}`;

            const { data } = await axios.get(endpoint);

            if (reset) {
                setFilterproduct(data.products);
                setSearchPage(2);
            } else {
                setFilterproduct(prev => [...prev, ...data.products]);
                setSearchPage(prev => prev + 1);
            }
            setHasMoreSearchResults(data.products.length > 0);
            setPage(prev => prev + 1);
        } catch (error: any) {
            toast.error(i18n.language === "fr" ? error.response.data.message : error.response.data.messageE)
        } finally {
            setLoading(false)
        }
    }, [i18n.language]);

    const handleValSearch = () => {
        handleSearch(1, true, valueToSearch, index);
    };

    const loadMoreSearchResults = useCallback(async () => {
        if (loading || !hasMoreSearchResults) return;
        await handleSearch(searchPage, false, valueToSearch, index);
    }, [searchPage, loading, hasMoreSearchResults, handleSearch, valueToSearch, index]);

    // Initialize Intersection Observer
    useEffect(() => {
        if (!loaderRef.current || !hasMore) return;
        const options = {
            root: null,
            rootMargin: '20px',
            threshold: 1.0
        };

        observer.current = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
                isSearching ? loadMoreSearchResults() : loadMoreProducts();
            }
        }, options);

        if (loaderRef.current) {
            observer.current.observe(loaderRef.current);
        }

        return () => {
            if (observer.current) {
                observer.current.disconnect();
            }
        };
    }, [loadMoreProducts, loadMoreSearchResults, hasMore, isSearching]);

    // Initial data load
    useEffect(() => {
        const fetchInitialData = async () => {
            setLoading(true);
            try {
                const { data } = await axios.get(`${getAllProductsSave}?limit=12&page=1`);
                setProducts(data.products);
                setFilterproduct(data.products);
                setPage(2);
            } catch (error: any) {
                toast.error(i18n.language === "fr" ? error.response.data.message : error.response.data.messageE)
            } finally {
                setLoading(false);
            }
        };

        fetchInitialData();
        updaCategory();
    }, []);

    const updateSubCategories = async (id: any) => {
        const currentLanguage = i18n.language;
        try {
            const { data } = await axios.get(`${categories_products}/${id}/subcategories`);
            setAllDataSubCat(data);
            setSubCategories(currentLanguage === "fr" ? data.data : data.dataE);
        } catch (error: any) {
            toast.error(i18n.language === "fr" ? error.response.data.message : error.response.data.messageE)
        }
    };

    const updaCategory = async () => {
        const currentLanguage = i18n.language;
        try {
            const { data } = await axios.get(getAllCategories);
            setAllDataCat(data);
            setCategories(currentLanguage === "fr" ? data.data : data.dataE);
        } catch (error: any) {
            toast.error(i18n.language === "fr" ? error.response.data.message : error.response.data.messageE)
        }
    };

    const getProduct = async (id: any, categoryName?: string) => {
        setPage(1);
        setIndex(id);
        setHasMore(true);
        setLoading(true);
        setSelectedCategory(categoryName || 'all');
        setIsFilterOpen(false);

        try {
            if (id !== 0) {
                const { data } = await axios.get(`${categories_products}/${id}/products?limit=12&page=1}`);
                setFilterproduct(data.products);
            } else {
                const { data } = await axios.get(`${getAllProductsSave}?limit=12&page=1`);
                setFilterproduct(data.products);
            }
        } catch (error: any) {
            toast.error(i18n.language === "fr" ? error.response.data.message : error.response.data.messageE)
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const currentLanguage = i18n.language;
        if (allDataSubCat) {
            setSubCategories(currentLanguage === "fr" ? allDataSubCat.data : allDataSubCat.dataE);
        }
        if (allDataCat) {
            setCategories(currentLanguage === "fr" ? allDataCat.data : allDataCat.dataE);
        }
    }, [i18n.language, allDataSubCat, allDataCat]);

    const handleChange = (value: string) => {
        setSearchQuery(value);
        const label = value.toLocaleLowerCase();
        let search: any;
        if (index !== 0) {
            const productCategorie = products.filter((item: any) => Number(item.category_id === index));
            search = productCategorie.filter((item: any) =>
                item.product_name.toLocaleLowerCase().includes(label) ||
                item.sale_name.toLocaleLowerCase().includes(label) ||
                item.variety.toLocaleLowerCase().includes(label)
            );
        } else {
            search = products.filter((item: any) =>
                item.product_name.toLocaleLowerCase().includes(label) ||
                item.sale_name.toLocaleLowerCase().includes(label) ||
                item.variety.toLocaleLowerCase().includes(label)
            );
        }
        setFilterproduct(search);
    };

    const updateParentState = (key: string, val: any) => {
        setValueToSearch({ ...valueToSearch, [key]: val });
    };

    const toggleFilter = () => {
        setIsFilterOpen(!isFilterOpen);
    };

    // Get selected category name
    const getSelectedCategoryName = () => {
        if (selectedCategory === 'all') return t('all_categorie');
        const cat = categories.find(c => c.category_id === index);
        return cat ? cat.category_name : t('all_categorie');
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
            {/* Modern Header */}
            <header className="bg-white/80 backdrop-blur-md border-b border-gray-200/50 sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center gap-4">
                            {/* <button
                                onClick={() => navigate(-1)}
                                className="p-2 hover:bg-gray-100 rounded-xl transition-all duration-300 group"
                            >
                                <FaArrowLeft className="text-green-600 group-hover:scale-110 transition-transform" />
                            </button> */}
                            <Breadcrumb />
                        </div>

                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setViewMode('grid')}
                                className={`p-2 rounded-xl transition-all duration-300 ${viewMode === 'grid'
                                    ? 'bg-green-500 text-white shadow-lg shadow-green-500/30'
                                    : 'hover:bg-gray-100 text-gray-600'
                                    }`}
                            >
                                <Grid size={18} />
                            </button>
                            <button
                                onClick={() => setViewMode('list')}
                                className={`p-2 rounded-xl transition-all duration-300 ${viewMode === 'list'
                                    ? 'bg-green-500 text-white shadow-lg shadow-green-500/30'
                                    : 'hover:bg-gray-100 text-gray-600'
                                    }`}
                            >
                                <List size={18} />
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Search Bar with Filter Toggle */}
            <div className="sticky top-16 z-40 px-10  bg-white/80 backdrop-blur-md border-b border-gray-200/50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div style={{ boxShadow: "0 10px 30px -6px rgba(21, 128, 61, 0.15), 0 4px 12px rgba(26,28,26,0.08)" }} className="relative bg-[#fff] font-dm border border-[#dfe1df] rounded-[12px] px-4 py-5">
                        <div className="bg-gradient-to-b from-primary via-third to-secondary h-[60px] w-1 self-center rounded-lg absolute left-0 top-1/2 -translate-y-1/2" />
                        {/* <div className="bg-gradient-to-b from-primary via-third to-secondary h-14 w-1.5 self-center rounded-lg absolute right-0 top-1/2 -translate-y-1/2" /> */}
                        <div className="flex flex-col md:flex-row items-center gap-3">
                            <div className="flex w-full gap-3 flex-1">
                                <div className="flex-1 relative group">
                                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-[#93C01F] transition-colors" size={18} />
                                    <input
                                        value={searchQuery}
                                        onChange={(e) => handleChange(e.target.value)}
                                        placeholder={t("mesRequettes.rechercherPlaceholder")}
                                        className="w-full pl-12 pr-4 py-3 bg-[#f7f8f7] border border-gray-300 rounded-xl focus:bg-white focus:border-[#93C01F] focus:outline-none focus:ring-4 focus:ring-[#93C01F]/10 transition-all duration-300 text-sm placeholder:text-gray-400"
                                    />
                                </div>
                                <button
                                    onClick={toggleFilter}
                                    className={`p-3 rounded-xl transition-all duration-300 flex items-center gap-2 flex-shrink-0 ${isFilterOpen
                                        ? 'text-white bg-[#93C01F] shadow-lg shadow-[#93C01F]/30'
                                        : 'bg-[#f7f8f7] border border-gray-300 hover:bg-gray-100 text-gray-700'
                                        }`}
                                >
                                    <Filter size={20} />
                                    <span className="hidden sm:inline text-sm font-medium">
                                        {isFilterOpen ? t('close_filters') : t('filters')}
                                    </span>
                                    {isFilterOpen && <X size={16} />}
                                </button>
                            </div>

                            <CustomButton
                                onClick={() => setIsOpen(true)}
                                className="relative px-6 py-3 text-white rounded-xl transition-all duration-300 shadow-lg shadow-[#93C01F]/30 hover:shadow-xl hover:shadow-[#93C01F]/40 hover:-translate-y-0.5 flex items-center gap-2 w-full md:w-auto overflow-hidden"
                            >
                                <span className="absolute inset-0 bg-primary" />
                                <span className="absolute left-0 top-0 h-2 w-2 rounded-bl bg-secondary" />
                                <span className="absolute right-0 bottom-0 h-2 w-2 rounded-tr bg-third" />
                                <span className="relative text-sm font-medium">{t("recherche_avance")}</span>
                                <span className="relative"><ChevronRight size={16} /></span>
                            </CustomButton>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filter Panel - Slide Down */}
            <div className={`overflow-hidden px-10 transition-all duration-500 ease-in-out ${isFilterOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
                }`}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-4">
                    <div style={{ boxShadow: "0 10px 30px -6px rgba(21, 128, 61, 0.15), 0 4px 12px rgba(26,28,26,0.08)" }} className="relative bg-[#fff] font-dm border border-[#dfe1df] rounded-[12px] px-4 py-5">
                        <div className="bg-gradient-to-b from-primary via-third to-secondary h-24 w-1 self-center rounded-lg absolute right-0 top-1/2 -translate-y-1/2" />
                        {/* `<div className="bg-gradient-to-b from-primary via-third to-secondary h-20 w-1 self-center rounded-lg absolute right-0 top-1/2 -translate-y-1/2" /> */}
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                            <button
                                onClick={() => getProduct(0, 'all')}
                                className={`relative px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 overflow-hidden ${selectedCategory === 'all'
                                    ? 'text-white bg-[#93C01F] shadow-lg shadow-[#93C01F]/30 after:absolute after:right-0 after:top-0 after:h-1/2 after:w-1 after:rounded-r after:bg-secondary before:absolute before:left-0 before:bottom-0 before:h-1/2 before:w-1 before:rounded-l before:bg-third'
                                    : 'bg-[#f7f8f7] border border-gray-200 hover:bg-gray-100 hover:text-[#93C01F] text-gray-700'
                                    }`}
                            >
                                {t("all_categorie")}
                            </button>
                            {categories.map((categorie) => (
                                <button
                                    key={categorie.category_id}
                                    onClick={() => {
                                        updateSubCategories(categorie.category_id);
                                        getProduct(categorie.category_id, categorie.category_name);
                                    }}
                                    className={`relative px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 overflow-hidden ${selectedCategory === categorie.category_name
                                        ? 'text-white bg-[#93C01F] shadow-lg shadow-[#93C01F]/30 after:absolute after:right-0 after:top-0 after:h-1/2 after:w-1 after:rounded-r after:bg-secondary before:absolute before:left-0 before:bottom-0 before:h-1/2 before:w-1 before:rounded-l before:bg-third'
                                        : 'bg-[#f7f8f7] border border-gray-200 hover:bg-gray-100 hover:text-[#93C01F] text-gray-700'
                                        }`}
                                >
                                    {categorie.category_name}
                                </button>
                            ))}
                        </div>

                        {/* Subcategories */}
                        {subCategories.length > 0 && (
                            <div className="mt-4 pt-4 border-t border-gray-100">
                                <Nav
                                    products={products}
                                    setFilterproduct={setFilterproduct}
                                    t={t}
                                    value={value}
                                    setValue={setValue}
                                    subCategorie={subCategories}
                                />
                            </div>
                        )}

                        {/* Price Filter */}
                        <div className="mt-4 pt-4 border-t border-gray-100">
                            <div className="flex flex-col md:flex-row items-start md:items-center gap-3">
                                <button
                                    onClick={() => setIsPriceFilterOpen(!isPriceFilterOpen)}
                                    className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-[#93C01F] transition-colors whitespace-nowrap"
                                >
                                    {t("price")}
                                    <span className={`transition-transform duration-300 ${isPriceFilterOpen ? 'rotate-180' : ''}`}>
                                        {isPriceFilterOpen ? <FaChevronUp size={14} className="text-[#93C01F]" /> : <FaChevronDown size={14} className="text-gray-400" />}
                                    </span>
                                </button>

                                {isPriceFilterOpen && (
                                    <div className="w-full flex flex-col md:flex-row items-start md:items-center gap-3 flex-1">
                                        <div className="flex-1 min-w-0">
                                            <DraggableProgressBar
                                                min={100}
                                                max={100000}
                                                initialValue={100}
                                                onChange={(val: any) => updateParentState("prix_max", val)}
                                            />
                                        </div>
                                        <button
                                            onClick={handleValSearch}
                                            className="px-6 py-2.5 bg-[#93C01F] hover:bg-[#a7d135] text-white rounded-xl text-sm font-medium transition-all duration-300 shadow-md shadow-[#93C01F]/25 hover:shadow-lg hover:shadow-[#93C01F]/35 hover:-translate-y-0.5 flex-shrink-0"
                                        >
                                            {t("recherche")}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="flex flex-col">
                    {/* Results Header */}
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-3">
                            <h2 className="text-lg font-semibold text-gray-900">
                                {filterProduct.length} {t("products")}
                            </h2>
                            {selectedCategory !== 'all' && (
                                <span className="px-3 py-1 bg-green-50 text-green-700 rounded-full text-xs font-medium">
                                    {getSelectedCategoryName()}
                                </span>
                            )}
                        </div>
                        {loading && (
                            <div className="flex items-center gap-2">
                                <div className="w-5 h-5 border-2 border-green-500 border-t-transparent rounded-full animate-spin"></div>
                                <span className="text-sm text-gray-500">{t("loading")}</span>
                            </div>
                        )}
                    </div>

                    {/* Products Grid/List */}
                    <div className={`grid gap-6 ${viewMode === 'grid'
                        ? 'grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4'
                        : 'grid-cols-1'
                        }`}>
                        {filterProduct.map((product: any, index) => {
                            const images = JSON.parse(product.images);
                            return (
                                <div key={index} className="transform transition-all duration-300 hover:scale-[1.02] hover:shadow-xl">
                                    <ProductCard
                                        category_name={i18n.language === "fr" ? product.category_name : product.category_name_en}
                                        image={images[0]}
                                        uml={i18n.language === "fr" ? product.type : product.type_en}
                                        sub_category_name={i18n.language === "fr" ? product.sub_category_name : product.sub_category_name_en}
                                        title={i18n.language === "fr" ? product.product_name : product.product_name_en}
                                        quality={product.minimum_quantity}
                                        price={product.price}
                                        stock={product.stock ? product.stock.quantity : ""}
                                        quantity={product.stock ? product.stock.quantity : ""}
                                        productId={product.product_id}
                                        seller={product.producer_name}
                                    />
                                </div>
                            );
                        })}
                    </div>

                    {/* Loader */}
                    <div ref={loaderRef} className="w-full flex justify-center py-12">
                        {loading ? (
                            <div className="flex flex-col items-center gap-3">
                                <div className="relative">
                                    <div className="w-12 h-12 border-4 border-green-200 rounded-full"></div>
                                    <div className="absolute top-0 left-0 w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div>
                                </div>
                                <span className="text-sm text-gray-500 font-medium">{t("loading_more")}</span>
                            </div>
                        ) : !hasMore && filterProduct.length > 0 && (
                            <div className="flex flex-col items-center gap-2">
                                <div className="w-16 h-0.5 bg-gray-200 rounded-full"></div>
                                <p className="text-sm text-gray-400">{t("no_more_products")}</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Modal */}
            <Modal
                handleSearch={handleValSearch}
                setValueToSearch={updateParentState}
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                title={t("recherche_avance")}
                size="lg"
            />
        </div>
    )
}

export default Products